import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { CustomerShell } from "@/components/layout/CustomerShell";
import { OwnerShell } from "@/components/layout/OwnerShell";
import Marketing from "@/pages/Marketing";
import Pricing from "@/pages/Pricing";
import Marketplace from "@/pages/Marketplace";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import InfoPage from "@/pages/InfoPage";
import Products from "@/pages/Products";
import Solutions from "@/pages/Solutions";
import Learning from "@/pages/Learning";
import LearningArticle from "@/pages/LearningArticle";
import Opportunities from "@/pages/Opportunities";
import NotFound from "@/pages/NotFound";

// Customer pages
import CustomerHome from "@/pages/customer/Home";
import AdaptiveOS from "@/pages/AdaptiveOS";
import FundingReadiness from "@/pages/FundingReadiness";
import EstablishCredit from "@/pages/EstablishCredit";
import CreditReports from "@/pages/CreditReports";
import TradeLines from "@/pages/TradeLines";
import CashFlow from "@/pages/CashFlow";
import FinancialGraph from "@/pages/FinancialGraph";
import CapitalMarketplace from "@/pages/CapitalMarketplace";
import Documents from "@/pages/Documents";
import BusinessProfile from "@/pages/customer/BusinessProfile";
import BusinessCredit from "@/pages/customer/BusinessCredit";
import BusinessCreditHub from "@/pages/customer/BusinessCreditHub";
import FundingReadinessPro from "@/pages/customer/FundingReadinessPro";
import MembershipMatrix from "@/pages/owner/MembershipMatrix";
import CapitalPassport from "@/pages/CapitalPassport";
import ApprovalInbox from "@/pages/ApprovalInbox";
import Privacy from "@/pages/Privacy";
import Billing from "@/pages/Billing";

// Owner pages
import MissionControl from "@/pages/MissionControl";
import OwnerOverview from "@/pages/owner/Overview";
import CommandCenter from "@/pages/owner/CommandCenter";
import CeoOverview from "@/pages/owner/OwnerOverview";
import ProviderCommand from "@/pages/owner/ProviderCommand";
import ApprovalsCenter from "@/pages/owner/ApprovalsCenter";
import RevenueIntelligence from "@/pages/owner/RevenueIntelligence";
import ControlRoom from "@/pages/owner/ControlRoom";
import AIEmployees from "@/pages/owner/AIEmployees";
import Missions from "@/pages/owner/Missions";
import MissionDetail from "@/pages/owner/MissionDetail";
import EmployeeDetail from "@/pages/owner/EmployeeDetail";
import CustomerDetail from "@/pages/owner/CustomerDetail";
import Customers from "@/pages/owner/Customers";
import AIWorkforce from "@/pages/AIWorkforce";
import DataIntegrity from "@/pages/DataIntegrity";
import GovernanceCenter from "@/pages/GovernanceCenter";
import PlatformSettings from "@/pages/owner/PlatformSettings";
import BusinessCreditAdmin from "@/pages/owner/BusinessCreditAdmin";
import PreviewCustomer from "@/pages/owner/PreviewCustomer";
import Legal from "@/pages/owner/Legal";
import OperatingMode from "@/pages/owner/OperatingMode";
import Situations from "@/pages/owner/Situations";
import ContentStudio from "@/pages/owner/ContentStudio";
import CompetitorWatch from "@/pages/owner/CompetitorWatch";
import AIGovernance from "@/pages/owner/AIGovernance";
import AISafety from "@/pages/owner/AISafety";
import ImageStudio from "@/pages/owner/ImageStudio";
import AIModels from "@/pages/owner/AIModels";
import DocumentsApplications from "@/pages/owner/DocumentsApplications";
import MarketingEmail from "@/pages/owner/MarketingEmail";
import Infrastructure from "@/pages/owner/Infrastructure";
import ActivityReports from "@/pages/owner/ActivityReports";
import FinanceFunding from "@/pages/owner/FinanceFunding";
import WebsitesFunnels from "@/pages/owner/WebsitesFunnels";
import FinOps from "@/pages/owner/FinOps";
import CreditBuilder from "@/pages/customer/CreditBuilder";
import CreditBuilderAdmin from "@/pages/owner/CreditBuilderAdmin";
import LearningAdmin from "@/pages/owner/LearningAdmin";
import Integrations from "@/pages/owner/Integrations";
import RentBillReporting from "@/pages/customer/RentBillReporting";
import RentBillQueue from "@/pages/owner/RentBillQueue";
import AIWorkforceCommand from "@/pages/owner/AIWorkforceCommand";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";

function Spinner() {
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4 bg-[#060A14]" data-testid="app-first-paint-loader">
      <div className="relative h-14 w-14">
        <div className="absolute inset-0 rounded-full" style={{ background: "conic-gradient(from 0deg, #1a5fd0, #5a8bff, #d8b25a, #1a5fd0)", WebkitMask: "radial-gradient(farthest-side, transparent calc(100% - 4px), #000 0)", mask: "radial-gradient(farthest-side, transparent calc(100% - 4px), #000 0)", animation: "spin 1.1s linear infinite" }} />
        <div className="absolute inset-0 flex items-center justify-center"><span className="h-2 w-2 rounded-full bg-[#d8b25a]" style={{ animation: "pulse 1.4s ease-in-out infinite" }} /></div>
      </div>
      <div className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-slate-500">Millionaire Mindsetters</div>
    </div>
  );
}

// Any authenticated user (customer or admin).
function RequireAuth({ children }) {
  const { user } = useAuth();
  if (user === null) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Admins only. Customers are bounced to their portal.
function RequireAdmin({ children }) {
  const { user } = useAuth();
  if (user === null) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/home" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <Spinner />;
  if (user) return <Navigate to={user.role === "admin" ? "/owner" : "/home"} replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public */}
            <Route path="/" element={<Marketing />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/marketplace" element={<Marketplace />} />
            <Route path="/products" element={<Products />} />
            <Route path="/solutions" element={<Solutions />} />
            <Route path="/learning" element={<Learning />} />
            <Route path="/learning/:slug" element={<LearningArticle />} />
            <Route path="/opportunities" element={<Opportunities />} />
            <Route path="/info/:slug" element={<InfoPage />} />
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />

            {/* Customer portal */}
            <Route element={<RequireAuth><CustomerShell /></RequireAuth>}>
              <Route path="/home" element={<CustomerHome />} />
              <Route path="/credit-builder" element={<CreditBuilder />} />
              <Route path="/adaptive" element={<AdaptiveOS />} />
              <Route path="/funding-readiness" element={<FundingReadinessPro />} />
              <Route path="/funding-readiness/legacy" element={<FundingReadiness />} />
              <Route path="/establish-credit" element={<EstablishCredit />} />
              <Route path="/credit-reports" element={<CreditReports />} />
              <Route path="/trade-lines" element={<TradeLines />} />
              <Route path="/tradelines" element={<Navigate to="/trade-lines" replace />} />
              <Route path="/rent-bill-reporting" element={<RentBillReporting />} />
              <Route path="/business-credit" element={<BusinessCreditHub />} />
              <Route path="/business-credit/setup" element={<BusinessCredit />} />
              <Route path="/cash-flow" element={<CashFlow />} />
              <Route path="/financial-graph" element={<FinancialGraph />} />
              <Route path="/funding-options" element={<CapitalMarketplace />} />
              <Route path="/documents" element={<Documents />} />
              <Route path="/business-identity" element={<BusinessProfile />} />
              <Route path="/capital-passport" element={<CapitalPassport />} />
              <Route path="/approvals" element={<ApprovalInbox />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/billing" element={<Billing />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
            </Route>

            {/* Owner / admin portal */}
            <Route element={<RequireAdmin><OwnerShell /></RequireAdmin>}>
              <Route path="/owner" element={<CommandCenter />} />
              <Route path="/owner/overview" element={<OwnerOverview />} />
              <Route path="/owner/control-room" element={<ControlRoom />} />
              <Route path="/owner/employees" element={<AIEmployees />} />
              <Route path="/owner/employees/:id" element={<EmployeeDetail />} />
              <Route path="/owner/missions" element={<Missions />} />
              <Route path="/owner/missions/:id" element={<MissionDetail />} />
              <Route path="/owner/ceo" element={<CeoOverview />} />
              <Route path="/owner/provider-command" element={<ProviderCommand />} />
              <Route path="/owner/approvals-center" element={<ApprovalsCenter />} />
              <Route path="/owner/revenue-intel" element={<RevenueIntelligence />} />
              <Route path="/owner/legacy-overview" element={<MissionControl />} />
              <Route path="/owner/customers" element={<Customers />} />
              <Route path="/owner/customers/:id" element={<CustomerDetail />} />
              <Route path="/owner/approvals" element={<ApprovalInbox />} />
              <Route path="/owner/ai-workforce" element={<AIWorkforceCommand />} />
              <Route path="/owner/ai-workforce/agents" element={<AIWorkforce />} />
              <Route path="/owner/rent-bill" element={<RentBillQueue />} />
              <Route path="/owner/data" element={<DataIntegrity />} />
              <Route path="/owner/integrations" element={<Infrastructure />} />
              <Route path="/owner/websites" element={<WebsitesFunnels />} />
              <Route path="/owner/marketing" element={<MarketingEmail />} />
              <Route path="/owner/credit" element={<BusinessCreditAdmin />} />
              <Route path="/owner/credit-builder" element={<CreditBuilderAdmin />} />
              <Route path="/owner/finance" element={<FinanceFunding />} />
              <Route path="/owner/documents" element={<DocumentsApplications />} />
              <Route path="/owner/activity" element={<ActivityReports />} />
              <Route path="/owner/funding" element={<CapitalMarketplace />} />
              <Route path="/owner/billing" element={<Billing />} />
              <Route path="/owner/legal" element={<Legal />} />
              <Route path="/owner/operating-mode" element={<OperatingMode />} />
              <Route path="/owner/situations" element={<Situations />} />
              <Route path="/owner/content" element={<ContentStudio />} />
              <Route path="/owner/learning" element={<LearningAdmin />} />
              <Route path="/owner/providers" element={<Integrations />} />
              <Route path="/owner/competitors" element={<CompetitorWatch />} />
              <Route path="/owner/ai-governance" element={<AIGovernance />} />
              <Route path="/owner/ai-safety" element={<AISafety />} />
              <Route path="/owner/image-studio" element={<ImageStudio />} />
              <Route path="/owner/ai-models" element={<AIModels />} />
              <Route path="/owner/finops" element={<FinOps />} />
              <Route path="/owner/security" element={<GovernanceCenter />} />
              <Route path="/owner/memberships" element={<MembershipMatrix />} />
              <Route path="/owner/settings" element={<PlatformSettings />} />
              <Route path="/owner/preview" element={<PreviewCustomer />} />
            </Route>

            {/* Legacy owner URLs → owner portal */}
            <Route path="/mission-control" element={<Navigate to="/owner" replace />} />
            <Route path="/ai-workforce" element={<Navigate to="/owner/ai-workforce" replace />} />
            <Route path="/providers" element={<Navigate to="/owner/integrations" replace />} />
            <Route path="/data-integrity" element={<Navigate to="/owner/data" replace />} />
            <Route path="/governance" element={<Navigate to="/owner/security" replace />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </AuthProvider>
    </div>
  );
}

export default App;
